import styled from 'styled-components';

/* Creating a project is a modal-sized amount of content — three cards, a name, a period, a list of
   funds — and it was never the drawer that made the old one hard. It was two screens instead of one,
   and a textarea of comma-separated names with an echo of what the split produced underneath it.

   One screen now, and split at the place the work splits: on the left what you type, on the right
   what has accumulated. The report type sits above both, because choosing it renames half of what
   is under it.

   The geometry is Source Files': hairline gray-2 borders, radius 8, selection shown by the border. */

const BORDER = '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5))';
const PANEL = 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-7))';

/* Two halves, divided by one rule that runs the height of the body: on the left everything you
   choose and type, on the right the funds as they accumulate. The modal carries no padding of its
   own — each half has its own — so the divider meets the header above it and the footer below it
   with nothing in between. */
const QUIET_SCROLLBAR = `
  @supports not selector(::-webkit-scrollbar) {
    scrollbar-color: light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4)) transparent;
  }

  &::-webkit-scrollbar {
    width: var(--dx-scrollbar-size);
    height: var(--dx-scrollbar-size);
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4));
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  &:hover::-webkit-scrollbar-thumb {
    background: light-dark(var(--mantine-color-gray-4), var(--mantine-color-gray-5));
    background-clip: content-box;
  }
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  /* One geometry for every control here: Mantine's defaults were a second visual language next to
     the cards and the rows. */
  .mantine-Input-input {
    min-height: 38px;
    border-radius: 8px;
    border: ${BORDER};
    font-size: 13px;
  }

  .mantine-InputWrapper-label {
    margin-bottom: 5px;
    font-size: 13px;
    font-weight: 600;
    color: light-dark(var(--mantine-color-dark-7), var(--mantine-color-gray-0));
  }

  .mantine-InputWrapper-description {
    font-size: 11px;
  }
`;

export const Split = styled.div`
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  border-top: ${BORDER};
`;

export const Working = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px;
  overflow-y: auto;
  ${QUIET_SCROLLBAR}
`;

/* The funds as they accumulate. Fifty of them is an ordinary period, so the list scrolls inside the
   half rather than growing the modal past the window. */
export const Collected = styled.div`
  flex: 0 0 372px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  padding: 18px 0;
  border-left: ${BORDER};
`;

export const CollectedList = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  ${QUIET_SCROLLBAR}

  /* Nothing added yet — and the field beside it already says what to do about that. */
  .empty {
    display: block;
    padding: 16px 18px;
    font-size: 11px;
    color: light-dark(var(--mantine-color-gray-5), var(--mantine-color-dark-2));
  }
`;

export const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-4));
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;

  .grow {
    flex: 1 1 200px;
    min-width: 0;
  }
`;

/* The period and the year are one click each, not a click to open and a click to choose: nine
   quarters and five years all fit on a line, and which one is set is readable without opening
   anything. */
export const Choice = styled.div`
  .label {
    margin-bottom: 5px;
    font-size: 13px;
    font-weight: 600;
    color: light-dark(var(--mantine-color-dark-7), var(--mantine-color-gray-0));
  }

  .mantine-SegmentedControl-root {
    padding: 3px;
    border: ${BORDER};
    border-radius: 8px;
    background: light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6));
  }

  .mantine-SegmentedControl-label {
    padding: 5px 10px;
    font-size: 13px;
    line-height: 18px;
  }

  .mantine-SegmentedControl-indicator {
    border-radius: 6px;
  }
`;

export const TypeCards = styled.div`
  display: flex;
  gap: 8px;

  /* Selection by border, the way a fund is selected on the pages this one leads to. */
  button {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 9px 11px;
    border: ${BORDER};
    border-radius: 8px;
    background: ${PANEL};
    text-align: left;
    cursor: pointer;
    transition:
      border-color 120ms ease,
      background 120ms ease;

    &:hover {
      border-color: light-dark(var(--mantine-color-gray-4), var(--mantine-color-dark-3));
    }

    &[data-active='true'] {
      border-color: light-dark(var(--mantine-color-blue-4), var(--mantine-color-blue-6));
      background: light-dark(var(--mantine-color-blue-0), rgba(34, 139, 230, 0.08));
    }

    .title {
      font-size: 13px;
      font-weight: 600;
      line-height: 18px;
      color: light-dark(var(--mantine-color-dark-7), var(--mantine-color-gray-0));
    }

    .description {
      font-size: 11px;
      line-height: 15px;
      color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
    }
  }
`;

export const FormPFBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 14px;
  border: ${BORDER};
  border-radius: 8px;
  background: light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6));

  /* The block's ground is gray-0, so the controls standing on it are white or they sink into it. */
  .mantine-SegmentedControl-root {
    background: light-dark(var(--mantine-color-white), var(--mantine-color-dark-7));
  }

  /* The calculator opens here rather than in a window of its own, in this block's sizes rather than
     its own — and its table stays away until it has rows to show. */
  .mantine-InputWrapper-label {
    font-size: 12px;
  }

  .mantine-Accordion-root .mantine-InputWrapper-label {
    white-space: nowrap;
  }

  /* The calculator sets its cell sizes inline, so only !important reaches them. */
  .mantine-Table-th,
  .mantine-Table-td {
    padding: 5px 8px !important;
    word-break: normal;
  }

  .mantine-Table-th {
    font-size: 11px !important;
  }

  .mantine-Table-td {
    font-size: 12px !important;
  }

  table:has(tbody:empty) {
    display: none;
  }

  /* A card of its own, like the choices above it, and the calculator opens inside it. */
  .calculator {
    border: ${BORDER};
    border-radius: 8px;
    background: ${PANEL};
    overflow: hidden;
    transition: border-color 120ms ease;

    &:hover,
    &[data-open='true'] {
      border-color: light-dark(var(--mantine-color-gray-4), var(--mantine-color-dark-3));
    }
  }

  .calculatorHead {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    text-align: left;

    .tile {
      flex: none;
      display: grid;
      place-items: center;
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: light-dark(var(--mantine-color-blue-0), rgba(34, 139, 230, 0.12));
      color: light-dark(var(--mantine-color-blue-6), var(--mantine-color-blue-4));
    }

    .copy {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    .title {
      font-size: 13px;
      font-weight: 600;
      line-height: 18px;
      color: light-dark(var(--mantine-color-dark-7), var(--mantine-color-gray-0));
    }

    .description {
      font-size: 11px;
      line-height: 15px;
      color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
    }

    .chevron {
      flex: none;
      color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
      transition: transform 120ms ease;
    }
  }

  .calculator[data-open='true'] .chevron {
    transform: rotate(180deg);
  }

  .calculatorBody {
    padding: 12px;
    border-top: ${BORDER};
  }
`;

export const Note = styled.div`
  font-size: 11px;
  line-height: 16px;
  color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: none;
  padding: 12px 20px;
  border-top: ${BORDER};

  .recap {
    font-size: 12px;
    color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
  }

  /* A refusal reads in the same line the recap was in — the button did nothing, and this says why. */
  .recap.refused {
    color: light-dark(var(--mantine-color-red-7), var(--mantine-color-red-4));
    max-width: 640px;
  }

  /* The way back to the form this one replaces, in the one place a modal has for it. */
  .legacy {
    font-size: 11px;
    text-decoration: underline;
    color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
  }
`;
